import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TrendingUp, Clock, Image as ImageIcon, Trophy, Info } from 'lucide-react';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { formatPercent as fmtPct } from '../../../../utils/formatters';
import { Card } from '../../../../components/ui/Card';
import { Table, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { Badge } from '../../../../components/ui/Badge';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import KpiCard from '../components/KpiCard';
import QualityFunnel from '../components/QualityFunnel';
import { KpiRowSkeleton, SkeletonPanel } from './TabSkeletons';
import { CRITERION_LABEL } from '../utils/criteriaLabels';

// Escala de calor por bucket de score (paleta de recharts — excepción tolerada).
const BUCKET_COLORS = {
  '0-20': '#DC2626',
  '21-40': '#F97316',
  '41-60': '#FBBF24',
  '61-80': '#84CC16',
  '81-100': '#10B981',
};

// Mantengo la misma forma {key: label} para no tocar el resto del render,
// pero filtro los criterios visibles en el tab Global (omito image_count,
// que ya no pondera al totalScore).
const SUBSCORE_LABELS = {
  rut: CRITERION_LABEL.rut,
  receiver: CRITERION_LABEL.receiver,
  distance: CRITERION_LABEL.distance,
  timeliness: CRITERION_LABEL.timeliness,
  images_ai: CRITERION_LABEL.images_ai,
};

const GlobalTab = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    deliveryQualityService
      .getGlobal(filters)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch((e) => {
        if (alive) setError(e?.message || 'Error');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filters]);

  const cfg = data?.criteriaConfig || {};
  const thresholds = cfg.thresholds || {};
  const weights = cfg.weights || {};

  const ruleByKey = useMemo(
    () => ({
      rut: thresholds.rut?.label || 'RUT chileno valido',
      receiver: thresholds.receiver?.label || 'Nombre del receptor presente',
      distance:
        thresholds.distance?.label ||
        `Coords <= ${thresholds.distance?.max_meters ?? 100} m del destino`,
      timeliness:
        thresholds.timeliness?.label ||
        `Entregado antes de ${thresholds.timeliness?.grace_hour ?? 21}:00`,
      image_count:
        thresholds.image_count?.label ||
        `>= ${thresholds.image_count?.min_count ?? 2} imagenes`,
      images_ai:
        thresholds.images_ai?.label ||
        'Analisis IA: etiqueta del paquete + numero de domicilio + receptor/lugar',
    }),
    [thresholds],
  );

  if (loading) {
    // Skeleton acorde al contenido: 4 KPIs + 2 paneles (chart + tabla).
    return (
      <>
        <QualityFunnel filters={filters} />
        <KpiRowSkeleton count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <SkeletonPanel height={372} />
          <SkeletonPanel height={372} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <QualityFunnel filters={filters} />
        <div className="rounded-lg border border-status-error-soft bg-status-error-soft text-status-error px-4 py-3 text-sm">
          {t('No se pudieron cargar los KPIs')}: {error}
        </div>
      </>
    );
  }

  if (!data || !data.total_evaluations) {
    return (
      <>
        <QualityFunnel filters={filters} />
        <div className="rounded-lg border border-border bg-bg-muted text-fg-muted px-4 py-3 text-sm">
          {t('Sin evaluaciones para el rango seleccionado.')}
        </div>
      </>
    );
  }

  const buckets = Object.entries(data.score_distribution || {}).map(([k, v]) => ({
    bucket: k,
    count: v,
  }));

  const ruleHintByKey = {
    rut: '(formato valido)',
    receiver: '(nombre presente)',
    distance: `(<= ${thresholds.distance?.max_meters ?? 100} m)`,
    timeliness: `(< ${thresholds.timeliness?.grace_hour ?? 21}:00)`,
    image_count: `(>= ${thresholds.image_count?.min_count ?? 2} img)`,
    images_ai: '(VALIDADA por IA externa)',
  };

  const renderDetail = (r) => {
    if (r.key === 'distance' && r.extras.avg_meters != null) {
      return t('{{n}} m promedio', { n: r.extras.avg_meters });
    }
    if (r.key === 'timeliness' && r.extras.late_count != null) {
      return t('{{n}} entregas tarde', { n: r.extras.late_count });
    }
    if (r.key === 'image_count' && r.extras.missing_count != null) {
      return t('{{n}} sin imag. minimas', { n: r.extras.missing_count });
    }
    if (r.key === 'images_ai') {
      const v = r.extras?.validated ?? 0;
      const d = r.extras?.doubtful ?? 0;
      const x = r.extras?.rejected ?? 0;
      return t('{{v}} val. / {{d}} dud. / {{x}} rech.', { v, d, x });
    }
    return '—';
  };

  const subRows = Object.entries(SUBSCORE_LABELS).map(([key, label]) => {
    const c = data.by_criterion?.[key] || {};
    return {
      key,
      label,
      avg: c.avg ?? 0,
      passRate: c.pass_rate ?? 0,
      extras: c,
      weight: weights[key],
    };
  });

  return (
    <>
      <QualityFunnel filters={filters} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <KpiCard title={t('Score promedio')} value={data.avg_score} sub={`${data.total_evaluations} ${t('evaluaciones')}`} tone="brand" icon={TrendingUp} />
        <KpiCard title={t('Entregas tarde')} value={data.late_count} sub={`> 22:00 hrs`} tone="error" icon={Clock} />
        <KpiCard title={t('Faltan imágenes')} value={data.missing_images_count} sub={t('Bajo el mínimo')} tone="warning" icon={ImageIcon} />
        <KpiCard title={t('Score min/max')} value={`${data.min_score} / ${data.max_score}`} sub={t('Rango observado')} tone="info" icon={Trophy} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
        <Card padding="none" className="h-full overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-bg-muted text-sm font-semibold text-fg">
            {t('Distribución de scores')}
          </div>
          <div className="p-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name={t('Paquetes')}>
                  {buckets.map((b) => (
                    <Cell key={b.bucket} fill={BUCKET_COLORS[b.bucket] || '#F97316'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="none" className="h-full overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-bg-muted flex justify-between items-center">
            <span className="text-sm font-semibold text-fg">{t('Desempeño por criterio')}</span>
            <small className="text-fg-subtle font-normal text-xs">{t('Umbrales configurables')}</small>
          </div>

          {isMobile ? (
            <div className="p-3 flex flex-col gap-2">
              {subRows.map((r) => (
                <div key={r.key} className="rounded-lg border border-border p-3 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-fg">{t(r.label)}</span>
                    {r.weight != null && (
                      <Badge tone="neutral">{t('peso')} {r.weight}</Badge>
                    )}
                    <span title={t(ruleByKey[r.key])} className="cursor-help text-fg-subtle">
                      <Info className="w-3 h-3" />
                    </span>
                  </div>
                  <div className="text-[11px] text-fg-subtle">{t(ruleHintByKey[r.key])}</div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-fg-muted">{t('Promedio')}</span>
                    <span className="font-semibold text-fg tabular-nums">{(r.avg ?? 0).toFixed(1)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">{t('% cumple')}</span>
                    <span className="font-semibold text-fg tabular-nums">{fmtPct(r.passRate)}</span>
                  </div>
                  <div className="text-xs text-fg-subtle">{renderDetail(r)}</div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('Criterio')}</TH>
                  <TH className="text-right" title={t('Score promedio del criterio (0-100)')}>
                    {t('Promedio')}
                  </TH>
                  <TH className="text-right" title={t('Porcentaje de entregas que superan el umbral binario del criterio.')}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {t('% cumple')} <Info className="w-3 h-3 opacity-60" />
                    </span>
                  </TH>
                  <TH className="text-right">{t('Detalle')}</TH>
                </TR>
              </THead>
              <TBody>
                {subRows.map((r) => (
                  <TR key={r.key}>
                    <TD>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{t(r.label)}</span>
                        {r.weight != null && (
                          <Badge tone="neutral">{t('peso')} {r.weight}</Badge>
                        )}
                        <span title={t(ruleByKey[r.key])} className="cursor-help text-fg-subtle">
                          <Info className="w-3 h-3" />
                        </span>
                      </div>
                      <div className="text-[11px] text-fg-subtle mt-0.5">{t(ruleHintByKey[r.key])}</div>
                    </TD>
                    <TD className="text-right tabular-nums">{(r.avg ?? 0).toFixed(1)}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.passRate)}</TD>
                    <TD className="text-right text-xs text-fg-subtle">{renderDetail(r)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
};

export default GlobalTab;
